import { FilterDropdown } from '@/shared/components/FilterDropdown'
import { ApprovalDetailDialog } from '@/shared/components/legal-documents/ApprovalDetailDialog'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { useApproveRequest } from '@/shared/hooks/request-approvals/useApproveRequest'
import { useGetRequestApprovals } from '@/shared/hooks/request-approvals/useGetRequestApprovals'
import { useRejectRequest } from '@/shared/hooks/request-approvals/useRejectRequest'
import { useAreas } from '@/shared/hooks/useAreas'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useRegions } from '@/shared/hooks/useRegions'
import { api } from '@/shared/lib/api'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { getBorderColorByLevel } from '@/shared/utils/index.utils'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { RequestApproval, RequestApprovalStatus } from '@workspace/lib/api'
import { UserAvatar } from '@workspace/ui/components/UserAvatar'
import { Badge } from '@workspace/ui/components/Badge'
import { Button } from '@workspace/ui/components/Button'
import { DataTable, type DataTableSorting } from '@workspace/ui/components/DataTable'
import { Menu, MenuHeader, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import dayjs from 'dayjs'
import { MoreHorizontal } from 'lucide-react'
import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PreviewSearchItem } from '@/shared/components/previewSearchItem'

export const Route = createFileRoute('/legal-documents/approvals/')({
    validateSearch: z.object({
        searchId: z.string().optional(),
        type: z.string().optional(),
        documentId: z.string().optional(),
        q: z.string().optional(),
        page: z.number().catch(1).optional(),
        pageSize: z.number().catch(10).optional(),
        regionValues: z.string().optional(),
        areaValues: z.string().optional(),
        statusValues: z.string().optional(),
        requesterValues: z.string().optional(),
        sortBy: z.string().optional(),
        sortDirection: z.string().optional(),
    }),
    component: ApprovalsPage,
    beforeLoad: () => requirePermissionBeforeLoad('view', 'task'),
})

const columnHelper = createColumnHelper<RequestApproval>()

const statusConfig: Record<RequestApprovalStatus, { label: string; className: string }> = {
    [RequestApprovalStatus.PENDING]: {
        label: 'Yêu cầu',
        className: 'bg-[#3B82F6] text-white',
    },
    [RequestApprovalStatus.IN_PROGRESS]: {
        label: 'Đang xử lý',
        className: 'bg-[#FFC20B] text-white',
    },
    [RequestApprovalStatus.APPROVED]: {
        label: 'Duyệt',
        className: 'bg-[#22C55E] text-white',
    },
    [RequestApprovalStatus.REJECTED]: {
        label: 'Từ chối',
        className: 'bg-[#EF4444] text-white',
    },
}

function ApprovalsPage() {
    const { t } = useTranslation()
    const searchParams = Route.useSearch()
    const navigate = Route.useNavigate()

    const searchQuery = searchParams.q || ''
    const currentPage = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const regionValues = searchParams.regionValues ? searchParams.regionValues.split(',') : []
    const areaValues = searchParams.areaValues ? searchParams.areaValues.split(',') : []
    const statusValues = searchParams.statusValues ? searchParams.statusValues.split(',') : []
    const requesterValues = searchParams.requesterValues ? searchParams.requesterValues.split(',') : []
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: (searchParams.sortDirection as 'asc' | 'desc') || 'desc' }
        : { sortBy: 'createdAt', sortDirection: 'desc' as const }

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }), replace: true })
    }

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setCurrentPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setRegionValues = (values: string[]) => {
        updateSearch({ regionValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    }
    const setAreaValues = (values: string[]) =>
        updateSearch({ areaValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setStatusValues = (values: string[]) =>
        updateSearch({ statusValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setRequesterValues = (values: string[]) =>
        updateSearch({ requesterValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [selectedApproval, setSelectedApproval] = useState<RequestApproval | null>(null)
    const [isDetailOpen, setIsDetailOpen] = useState(false)

    const [isRegionFilterOpen, setIsRegionFilterOpen] = useState(false)
    const [isAreaFilterOpen, setIsAreaFilterOpen] = useState(false)
    const [isRequesterFilterOpen, setIsRequesterFilterOpen] = useState(false)

    const debouncedSearch = useDebounce(searchQuery, 300)

    const { data, isLoading } = useGetRequestApprovals({
        q: debouncedSearch || undefined,
        page: currentPage,
        take: pageSize,
        regionIds: regionValues.length > 0 ? regionValues.join(',') : undefined,
        areaIds: areaValues.length > 0 ? areaValues.join(',') : undefined,
        statuses: statusValues.length > 0 ? statusValues.join(',') : undefined,
        requesterIds: requesterValues.length > 0 ? requesterValues.join(',') : undefined,
        orderBy: sorting?.sortBy,
        order: sorting?.sortDirection === 'desc' ? 'DESC' : 'ASC',
    })

    const approveMutation = useApproveRequest()

    const rejectMutation = useRejectRequest()

    const statusOptions = [
        { id: RequestApprovalStatus.PENDING, name: 'Yêu cầu' },
        { id: RequestApprovalStatus.IN_PROGRESS, name: 'Đang xử lý' },
        { id: RequestApprovalStatus.APPROVED, name: 'Duyệt' },
        { id: RequestApprovalStatus.REJECTED, name: 'Từ chối' },
    ]

    // Fetch users for requester filter dropdown
    const { data: usersData, isLoading: isLoadingRequesters } = useQuery({
        queryKey: ['users-for-filter'],
        queryFn: () => api.user.listAll({ page: 1, take: 100 }),
        enabled: isRequesterFilterOpen,
    })

    const requesterOptions = usersData?.data.map(user => ({ id: user.id, name: user.fullName })) || []

    const { data: regions, isLoading: isLoadingRegions } = useRegions({
        enabled: isRegionFilterOpen,
    })

    const { data: areas, isLoading: isLoadingAreas } = useAreas({
        enabled: true,
    })

    const regionOptions = (regions?.data || []).map(region => ({ id: region.id, name: region.name }))

    const areaOptions = useMemo(() => {
        const allAreas = areas?.data || []
        if (regionValues.length === 0) return allAreas.map((area: any) => ({ id: area.id, name: area.name }))
        return allAreas
            .filter((area: any) => regionValues.includes(area.region?.id))
            .map((area: any) => ({ id: area.id, name: area.name }))
    }, [areas, regionValues])

    // Auto-prune areaValues when regionValues change
    useEffect(() => {
        if (!areas?.data || areaValues.length === 0) return

        if (regionValues.length === 0) {
            updateSearch({ areaValues: undefined })
            return
        }

        const validAreaIds = new Set(
            areas.data.filter((a: any) => regionValues.includes(a.region?.id)).map((a: any) => a.id),
        )

        const newAreaValues = areaValues.filter(id => validAreaIds.has(id))

        if (newAreaValues.length !== areaValues.length) {
            updateSearch({
                areaValues: newAreaValues.length > 0 ? newAreaValues.join(',') : undefined,
                page: 1,
            })
        }
    }, [regionValues, areaValues, areas])

    const totalResults = data?.meta?.itemCount || 0

    const handleViewDetail = (approval: RequestApproval) => {
        setSelectedApproval(approval)
        setIsDetailOpen(true)
    }

    const handleApprove = (id: string) => {
        approveMutation.mutate(id)
    }

    const handleReject = (id: string, reason: string) => {
        rejectMutation.mutate({ id, data: { comment: reason } })
    }

    const columns = [
        columnHelper.display({
            id: 'custom-actions',
            header: '',
            size: 48,
            enableSorting: false,
            meta: { className: '!max-w-[48px] !min-w-[48px] !w-[48px]' },
            cell: ({ row }) => {
                const approval = row.original
                return (
                    <MenuTrigger>
                        <Button variant="outline" size="iconSm" className="rounded-full border-foreground size-5">
                            <MoreHorizontal className="size-3!" />
                        </Button>
                        <MenuPopover className="w-[120px]">
                            <Menu>
                                <MenuHeader className="text-foreground">
                                    {t('common.actions.operation', 'Thao tác')}
                                </MenuHeader>
                                <MenuItem onAction={() => handleViewDetail(approval)} className="cursor-pointer">
                                    {t('common.actions.view', 'Xem')}
                                </MenuItem>
                            </Menu>
                        </MenuPopover>
                    </MenuTrigger>
                )
            },
        }),
        columnHelper.accessor('name', {
            header: t('approval.columns.content', 'Nội dung'),
            enableSorting: true,
            size: 320,
            meta: {
                headerClassName: 'font-bold text-sm text-text-table-head',
                noTitleTooltip: true,
            },
            cell: ({ row }) => {
                const approval = row.original
                return (
                    <div
                        className="flex items-start cursor-pointer hover:opacity-80"
                        onClick={() => handleViewDetail(approval)}
                    >
                        <span className="text-sm text-foreground truncate max-w-[280px]" title={approval.name}>
                            {approval.name}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'region-area',
            header: t('approval.columns.regionArea', 'Vùng - Khu vực'),
            enableSorting: false,
            size: 130,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const region = row.original.region?.name || '-'
                const area = row.original.area?.name || '-'
                return (
                    <div className="flex flex-col py-1 max-w-full">
                        <span className="text-sm font-medium text-foreground leading-5 truncate" title={region}>
                            {region}
                        </span>
                        <span className="text-xs text-muted-foreground leading-4 truncate" title={area}>
                            {area}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'status',
            header: t('common.fields.status', 'Trạng thái'),
            enableSorting: false,
            size: 120,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const status = row.original.status
                const config = statusConfig[status]

                return (
                    <Badge className={`h-5 px-2 py-0.5 text-xs font-medium rounded-full border-0 ${config.className}`}>
                        {config.label}
                    </Badge>
                )
            },
        }),
        columnHelper.display({
            id: 'requester',
            header: t('approval.columns.requester', 'Người yêu cầu'),
            enableSorting: false,
            size: 180,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const requester = row.original.requester
                if (!requester) return <span className="text-xs text-muted-foreground">-</span>

                return (
                    <div className="flex items-center gap-2">
                        <UserAvatar
                            src={requester.avatar}
                            alt={requester.fullName}
                            fallback={requester.fullName}
                            size="lg"
                            className="border-2 border-white"
                        />
                        <span className="text-sm text-foreground truncate" title={requester.fullName}>
                            {requester.fullName}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'approvers',
            header: t('approval.columns.approver', 'Người duyệt'),
            enableSorting: false,
            size: 120,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const levels = row.original.levels
                const approversWithLevel =
                    levels?.flatMap(l => l.approvers.map(a => ({ ...a, level: l.levelNumber }))) || []

                if (approversWithLevel.length === 0) return <span className="text-xs text-muted-foreground">-</span>

                return (
                    <div className="flex items-center -space-x-2">
                        {approversWithLevel.slice(0, 3).map(approver => (
                            <UserAvatar
                                key={approver.id}
                                src={approver.avatar}
                                alt={approver.fullName}
                                fallback={approver.fullName}
                                size="md"
                                className={`border-2 box-content ${getBorderColorByLevel(approver.level)}`}
                            />
                        ))}
                        {approversWithLevel.length > 3 && (
                            <div
                                className="relative z-10 size-8 box-content rounded-full bg-gray-110 flex items-center justify-center border-2 border-background"
                                title={approversWithLevel
                                    .slice(3)
                                    .map(a => a.fullName)
                                    .join(', ')}
                            >
                                <span className="text-[10px] text-gray-850 font-semibold">
                                    +{approversWithLevel.length - 3}
                                </span>
                            </div>
                        )}
                    </div>
                )
            },
        }),
        columnHelper.accessor('createdAt', {
            header: t('approval.columns.createdAt', 'Ngày cập nhật cuối'),
            enableSorting: true,
            size: 160,
            meta: {
                headerClassName: 'font-bold text-sm text-text-table-head',
                noTitleTooltip: true,
            },
            cell: ({ getValue }) => {
                const value = getValue()
                const formattedDate = value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'
                return (
                    <span className="text-sm text-foreground" title={formattedDate === '-' ? undefined : formattedDate}>
                        {formattedDate}
                    </span>
                )
            },
        }),
    ]

    return (
        <div className="flex flex-col gap-4 max-lg:gap-2 h-[calc(100vh-200px)] min-h-[500px] overflow-hidden">
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between">
                <h1 className="text-2xl font-bold">{t('approval.title', 'Phê duyệt')}</h1>
            </div>

            <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                {/* Search and Filters */}
                <div className="shrink-0 flex gap-4 flex-wrap w-full max-lg:gap-2">
                    <BsSearchField
                        autoTrim
                        className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                        containerClassName="max-lg:w-full"
                        placeholder={t('common.search', 'Tìm kiếm')}
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />

                    <FilterDropdown
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                        placeholder={t('common.fields.region', 'Vùng')}
                        inputPlaceholder={t('common.fields.region', 'Vùng')}
                        options={regionOptions}
                        selectedValues={regionValues}
                        onChange={setRegionValues}
                        setOpen={setIsRegionFilterOpen}
                        isLoading={isLoadingRegions}
                    />
                    <FilterDropdown
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                        placeholder={t('common.fields.area', 'Khu vực')}
                        inputPlaceholder={t('common.fields.area', 'Khu vực')}
                        options={areaOptions}
                        selectedValues={areaValues}
                        onChange={setAreaValues}
                        setOpen={setIsAreaFilterOpen}
                        isLoading={isLoadingAreas}
                        isDisabled={!regionValues || regionValues.length === 0}
                        disabledTooltip={t('common.filters.requireRegionFirst')}
                    />
                    <FilterDropdown
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                        placeholder={t('common.fields.status', 'Trạng thái')}
                        inputPlaceholder={t('common.fields.status', 'Trạng thái')}
                        options={statusOptions}
                        selectedValues={statusValues}
                        onChange={setStatusValues}
                    />
                    <FilterDropdown
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                        placeholder={t('approval.filters.requester', 'Người yêu cầu')}
                        inputPlaceholder={t('approval.filters.requester', 'Người yêu cầu')}
                        options={requesterOptions}
                        selectedValues={requesterValues}
                        onChange={setRequesterValues}
                        setOpen={setIsRequesterFilterOpen}
                        isLoading={isLoadingRequesters}
                    />
                </div>

                <DataTable
                    data={data?.data || []}
                    columns={columns}
                    enableRowSelection={false}
                    enableSorting={true}
                    sorting={sorting}
                    setSorting={setSorting}
                    isLoading={isLoading}
                    emptyString={t('common.emptyState', 'Không có kết quả')}
                    containerClassName="flex-1 overflow-y-auto min-h-0"
                />

                <div className="shrink-0">
                    <TableDataFooter
                        selectedCount={0}
                        onDelete={() => {}}
                        deleteDisabled={true}
                        totalCount={totalResults}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalPages={Math.ceil(totalResults / pageSize)}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={value => setPageSize(value || 10)}
                    />
                </div>

                <ApprovalDetailDialog
                    open={isDetailOpen}
                    onOpenChange={setIsDetailOpen}
                    approval={selectedApproval}
                    onApprove={handleApprove}
                    onReject={handleReject}
                />

                <PreviewSearchItem />
            </div>
        </div>
    )
}
